import { useEffect, useState } from 'react';
import { Img } from 'react-image';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import { Button, Modal, Spinner } from 'flowbite-react';
import { Loader } from 'ui';
import {
	CreateFeedbackQuestionResponseInput,
	CurrentWorkingStatus,
	FeedbackQuestionOption,
	FeedbackQuestionOptionDocument,
	IdealTimeDetails,
	UIFeedbackQuestion,
} from 'types';
import { useToast } from 'src/context/ToastProvider';
import { useGetAssessmentQuestionConfigQuery } from 'src/hooks/assessmentQuestionConfig.hooks';
import { useMutation, useQueryClient } from 'react-query';
import { createFeedbackQuestionResponse } from 'src/apis/feedbackQuestionResponse';
import { useMeetingContext } from 'src/context/MeetingProvider';
import { useUserContext } from 'src/context/UserProvider';
import { useGetAssessmentQuestionFeedbackByMeetingAndQuestionIdQuery } from 'src/hooks/assessmentQuestionFeedback.hooks';
import { createAssessmentQuestionFeedback } from 'src/apis/assessmentQuestionFeedback';
import NewCreatingPositionLoader from '../position/NewCreatingPositionLoader';

type CurrentAssessmentDetailsType = {
	tagRelationshipIds: string[];
	difficulty: number;
	questionTitle: string;
	questionId: string;
	sectionId: string;
	assessmentId: string;
	score: number;
};

const FeedbackQuestionsNudge = () => {
	const toast = useToast();
	const { id } = useParams();
	const queryClient = useQueryClient();
	const { isMentorLoggedIn } = useUserContext();
	const { meeting, ongoingAssessDetails, setHasAssessmentEnded, setPredictedUserSalary, setRecommendedUserPath } =
		useMeetingContext();

	const [isOpen, setIsOpen] = useState(false);
	const [timeDetails, setTimeDetails] = useState<IdealTimeDetails>({
		idealTime: 3600,
	});
	const [fQAgainstGroupIds, setFQAgainstGroupIds] = useState<Record<string, string[]>>({});
	const [hasNextQuestion] = useState<Boolean>(true);
	const [feedbackQuestions, setFeedbackQuestions] = useState<UIFeedbackQuestion[]>([]);
	const [assessmentQuestionConfigs, setAssessmentQuestionConfigs] = useState<string[]>([]);

	const [currentAssessmentDetails, setCurrentAssessmentDetails] = useState<CurrentAssessmentDetailsType>({
		tagRelationshipIds: [],
		difficulty: 0,
		assessmentId: '',
		questionId: '',
		questionTitle: '',
		sectionId: '',
		score: 0.0,
	});
	const [userOptionSelections, setUserOptionsSelections] = useState<CreateFeedbackQuestionResponseInput[]>([]);
	const isParticipantProfileIncomplete =
		!meeting?.participant?.profile.gradYear ||
		!meeting?.participant?.profile.currentWorkingStatus ||
		(meeting?.participant?.profile.currentWorkingStatus === CurrentWorkingStatus.Working &&
			!meeting?.participant?.profile.workExperiences?.length);

	const getAssessmentQuestions = useGetAssessmentQuestionConfigQuery(
		{
			tagRelationshipIds: currentAssessmentDetails.tagRelationshipIds,
			difficulty: currentAssessmentDetails.difficulty,
			assessmentTemplateId: String(ongoingAssessDetails?.interviewAssessmentTemplate) ?? '',
		},
		data => {
			const questions = data?.configs?.reduce((acc: UIFeedbackQuestion[], crr) => {
				crr?.feedbackQuestionGroup.feedbackQuestionWithGroupWeightage?.forEach(qWeightage => {
					const question = {
						_id: qWeightage.feedbackQuestion._id,
						title: qWeightage.feedbackQuestion.title,
						options: qWeightage.feedbackQuestion.options,
					} as UIFeedbackQuestion;
					if (fQAgainstGroupIds[qWeightage.feedbackQuestion._id]) {
						fQAgainstGroupIds[qWeightage.feedbackQuestion._id].push(crr?.feedbackQuestionGroup._id);
					} else {
						fQAgainstGroupIds[qWeightage.feedbackQuestion._id] = [crr?.feedbackQuestionGroup._id];
					}
					if (!acc.map(aQ => aQ._id).includes(qWeightage.feedbackQuestion._id)) {
						acc.push(question);
					}
				});
				return acc;
			}, []);

			setFQAgainstGroupIds({ ...fQAgainstGroupIds });
			setAssessmentQuestionConfigs(data?.configs?.map(curr => curr._id || '') || []);
			refetch();
			setFeedbackQuestions(questions || []);
		},
	);

	const onClose = () => {
		setIsOpen(false);
	};
	const onOpen = () => {
		setIsOpen(true);
	};

	const { refetch } = useGetAssessmentQuestionFeedbackByMeetingAndQuestionIdQuery(
		{
			questionId: currentAssessmentDetails.questionId,
			token: ongoingAssessDetails?.token ?? '',
			meetingId: meeting?._id ?? '',
			configs: assessmentQuestionConfigs,
		},
		data => {
			if (!data.assessmentQuestionFeedback && assessmentQuestionConfigs.length) {
				createAQF.mutate({
					questionId: currentAssessmentDetails.questionId,
					token: ongoingAssessDetails?.token ?? '',
					assessmentQuestionConfigs,
					currentIAT: String(ongoingAssessDetails?.interviewAssessmentTemplate) ?? '',
					meeting: meeting?._id ?? '',
					timeDetails,
				});
			}
		},
	);

	const createAQF = useMutation(['createAssessmentQuestionFeedback'], createAssessmentQuestionFeedback, {
		onSuccess(data) {},
	});

	const createFeedbackQuestionResponseFn = useMutation(
		['createFeedbackQuestionResponse'],
		createFeedbackQuestionResponse,
		{
			onSuccess(data) {
				queryClient.refetchQueries(['getOngoingAssessmentDetails']);
				queryClient.refetchQueries(['verifyCookie']);
				queryClient.refetchQueries(['getAssessmentQuestionConfig']);
				if (data.hasAssessmentEnded) {
					setHasAssessmentEnded(true);
					if (data.predictedUserSalary) setPredictedUserSalary(data.predictedUserSalary);
					if (data.recommendedUserPath) setRecommendedUserPath(data.recommendedUserPath);
					queryClient.refetchQueries(['getMeetingById']);
				}
				// setHasNextQuestion(!!data.data?.isNextQuestionPushed);
				toast({ title: 'Feedback Recorded', type: 'success' });
				setUserOptionsSelections([]);
				onClose();
			},
			onError(err: any) {
				setUserOptionsSelections([]);
				onClose();
				toast({ title: err.message, type: 'error' });
			},
		},
	);

	const handleOptionSelect = (feedbackQuestionId: string, optionMarks: number, optionBody: string) => {
		const isOptionSelected = userOptionSelections.find(fdQ => fdQ.feedbackQuestion === feedbackQuestionId);
		if (isOptionSelected) {
			const filteredFeedbackQuestionResponseData = userOptionSelections.map(fdQ =>
				fdQ.feedbackQuestion === feedbackQuestionId
					? {
							...fdQ,
							feedbackOptionSelected: { body: optionBody, marks: optionMarks } as FeedbackQuestionOption,
							score: optionMarks,
					  }
					: fdQ,
			);
			setUserOptionsSelections(filteredFeedbackQuestionResponseData);
		} else {
			setUserOptionsSelections((prev: CreateFeedbackQuestionResponseInput[]) => [
				...prev,
				{
					feedbackQuestion: feedbackQuestionId,
					feedbackOptionSelected: { body: optionBody, marks: optionMarks } as FeedbackQuestionOptionDocument,
					meeting: String(id || ''),
					score: optionMarks,
				},
			]);
		}
	};

	const handleCreateFeedbackQuestionResponse = () => {
		if (id)
			createFeedbackQuestionResponseFn.mutate({
				feedbackQuestionResponseInfo: userOptionSelections,
				meeting: id,
				assessmentQuestionConfigs,
				timeDetails,
				currentIAT: String(ongoingAssessDetails?.interviewAssessmentTemplate) ?? '',
				assessmentOptions: {
					token: ongoingAssessDetails?.token ?? '',
					order: ongoingAssessDetails?.order ?? 0,
					assessmentId: currentAssessmentDetails.assessmentId,
					sectionId: currentAssessmentDetails.sectionId,
					questionId: currentAssessmentDetails.questionId,
					addNextQuestionInSection: true,
				},
			});
	};

	useEffect(() => {
		window.addEventListener('message', e => {
			const data = e.data;
			if (data.type === 'current-question') {
				console.log('receiving uestion details');
				setTimeDetails({
					idealTime: data.currMeta?.meta?.idealTimeInSec ?? data.currMeta?.meta?.maxTimeInSec ?? 3600,
				});
				if (data.currentQuestionId) {
					setCurrentAssessmentDetails({
						tagRelationshipIds: data.currentQuestion.tagRelationships,
						difficulty: data.currentQuestion.difficulty,
						questionTitle: data.currentQuestion.title,
						questionId: data.currentQuestionId,
						sectionId: data.currentSectionId,
						assessmentId: data.currentAssessmentId,
						score: data.score || 0.0,
					});
				}
			} else if (data.type === 'assessment-ended') {
			}
		});
	}, []);

	if (!ongoingAssessDetails) {
		return null;
	}

	if (isMentorLoggedIn && isParticipantProfileIncomplete) return null;

	return (
		<>
			<div
				className={`flex h-[64px] w-[64px] items-center justify-center rounded-full bg-[#7777E7] ${
					meeting?.isAIInterview ? 'hidden' : ''
				}`}
				onClick={() => {
					onOpen();
				}}
			>
				<Img loading="lazy" className="py-7" src="/feedbackNudge.svg" width="38" height="38" alt="logo" />
			</div>

			<div data-testId="feedback-question-modal-container">
				<Modal
					size={'md'}
					show={isOpen}
					onClose={onClose}
					className="bg-opacity-0 pt-[220px]"
					position="bottom-right"
					style={{}}
				>
					<Modal.Header className="rounded-t-2xl bg-[#F2F6FF] text-lg font-semibold">
						<span className=" text-[#21191B] ">{currentAssessmentDetails.questionTitle || 'Go Next'}</span>
					</Modal.Header>
					<Modal.Body data-testid="feedback-question-container" className="h-[400px] overflow-y-scroll rounded-b-2xl">
						{feedbackQuestions.length <= 0 ? (
							<div className="flex items-center justify-center">
								<Loader />
							</div>
						) : (
							<div className="flex flex-col justify-start gap-4">
								{hasNextQuestion ? (
									feedbackQuestions.map(feedbackQuestion => (
										<div key={feedbackQuestion._id}>
											<p className="mb-1 text-sm font-semibold">
												{feedbackQuestion?.title}
												<span className="text-[#ED0331]">*</span>
											</p>
											<div className="flex flex-wrap gap-2">
												{feedbackQuestion?.options?.map(option => {
													const isOptionSelected = userOptionSelections.find(
														fdQ =>
															fdQ.feedbackOptionSelected.body === option.body &&
															fdQ.feedbackQuestion === feedbackQuestion._id,
													);
													return (
														<div key={option._id} className="group">
															<button
																onClick={() => handleOptionSelect(feedbackQuestion._id, option.marks, option.body)}
																className={classNames(
																	'mt-[4px] h-[40px] w-max min-w-fit cursor-pointer rounded-lg border-[1px] px-[10px] text-sm font-medium not-italic group-hover:border-blue-500',
																	{
																		'!cursor-default !border-blue-500': isOptionSelected,
																	},
																)}
															>
																{option.body}
															</button>
														</div>
													);
												})}
											</div>
										</div>
									))
								) : (
									<div className="mr-3">
										<NewCreatingPositionLoader
											showAnimation={true}
											progressBar={false}
											customClassCss="!w-[30%] mx-auto "
										/>
										{/* <Spinner size="sm" light={true} title="End interview" /> */}
									</div>
								)}
							</div>
						)}
					</Modal.Body>
					<Modal.Footer className="justify-center border-none">
						<Button
							color="purplePlaced"
							onClick={handleCreateFeedbackQuestionResponse}
							disabled={
								userOptionSelections.length < feedbackQuestions.length ||
								createFeedbackQuestionResponseFn.isLoading ||
								getAssessmentQuestions.isLoading
							}
						>
							{createFeedbackQuestionResponseFn.isLoading ? (
								<div className="mr-3">
									{/* <Spinner size="sm" light={true} /> */}
									<NewCreatingPositionLoader
										showAnimation={true}
										progressBar={false}
										customClassCss="!w-[30%] mx-auto "
									/>
								</div>
							) : null}
							{hasNextQuestion ? 'Submit and push next question' : 'End Interview'}
						</Button>
					</Modal.Footer>
				</Modal>
			</div>
		</>
	);
};

export default FeedbackQuestionsNudge;
